/* Hallmark · component: SearchableDropdown · genre: modern-minimal · theme: maeiz-tokens
 * Dropdown menu with inline search filter over items.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React, { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "../Button";
import { menuContentMotionClass, overlayCollision, thinScrollbarClass } from "../../styles/overlay";

export interface SearchableDropdownOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SearchableDropdownProps {
    options: SearchableDropdownOption[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    options,
    value,
    onChange,
    placeholder = "Choose…",
    searchPlaceholder = "Filter…",
    label,
    disabled,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const selected = options.find((o) => o.value === value);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    return (
        <div className={clsx("flex flex-col gap-1.5", className)}>
            {label ? <span className="text-sm font-medium text-neutral-dark dark:text-white">{label}</span> : null}
            <DropdownMenu.Root
                open={open}
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery("");
                }}
            >
                <DropdownMenu.Trigger asChild disabled={disabled}>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        className="w-full justify-between font-normal"
                        icon={ChevronDown}
                        iconPosition="end"
                    >
                        <span className={clsx("truncate", !selected && "text-neutral-secondary")}>
                            {selected?.label ?? placeholder}
                        </span>
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="start"
                        sideOffset={6}
                        avoidCollisions={overlayCollision.avoidCollisions}
                        collisionPadding={overlayCollision.collisionPadding}
                        className={clsx(
                            "z-[80] min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-xl border border-neutral-border bg-white shadow-mz-lg dark:border-white/12 dark:bg-neutral-navy",
                            menuContentMotionClass,
                        )}
                    >
                        <div className="flex items-center gap-2 border-b border-neutral-border px-3 py-2 dark:border-white/10">
                            <Search size={14} className="text-primary dark:text-gold-light" aria-hidden />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-secondary dark:text-white"
                                onKeyDown={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                        <div className={clsx("max-h-56 p-1", thinScrollbarClass)}>
                            {filtered.length === 0 ? (
                                <p className="px-2.5 py-2 text-sm text-neutral-secondary">No matches</p>
                            ) : (
                                filtered.map((opt) => (
                                    <DropdownMenu.Item
                                        key={opt.value}
                                        disabled={opt.disabled}
                                        className={clsx(
                                            "flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none",
                                            "data-[highlighted]:bg-neutral-light dark:data-[highlighted]:bg-white/10",
                                            opt.value === value && "text-primary dark:text-gold-light",
                                        )}
                                        onSelect={() => onChange?.(opt.value)}
                                    >
                                        <Check
                                            size={14}
                                            className={clsx(opt.value === value ? "opacity-100" : "opacity-0")}
                                        />
                                        {opt.label}
                                    </DropdownMenu.Item>
                                ))
                            )}
                        </div>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
};
